/**
 * Header bell — the AI assistant's inbox (scheduled task results, reminders).
 *
 * Fed by `useAiNotifications` (mirrored from the embedded chat widget's
 * server-side inbox), so a task that finished at 07:00 while the widget was
 * collapsed or the tab was closed is still visible here. Clicking an item
 * opens its thread in the assistant and marks it read.
 */
import React, { useMemo, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Badge,
    Box,
    Button,
    ButtonBase,
    ClickAwayListener,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Popper,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AlarmIcon from '@mui/icons-material/Alarm';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import { useAiConfig } from '../../../../hooks/useAiConfig';
import { useAiNotifications } from '../../../../hooks/useAiNotifications';
import type { AiHostNotification } from '../../../../hooks/useAiNotifications';
import { aiOpenView } from '../../../../hooks/useAiAssistant';

function kindIcon(n: AiHostNotification) {
    if (n.kind === 'task_failed' || n.severity === 'error') return <ErrorOutlineIcon color="error" fontSize="small" />;
    if (n.kind === 'task_done' || n.severity === 'success') return <CheckCircleOutlineIcon color="success" fontSize="small" />;
    if (n.kind === 'reminder') return <AlarmIcon color="warning" fontSize="small" />;
    return <InfoOutlinedIcon color="info" fontSize="small" />;
}

function relativeTime(iso: string): string {
    const t = +new Date(iso);
    if (!t) return '';
    const diffMin = Math.round((Date.now() - t) / 60_000);
    if (diffMin < 1) return 'עכשיו';
    if (diffMin < 60) return `לפני ${diffMin} דק׳`;
    const h = Math.round(diffMin / 60);
    if (h < 24) return `לפני ${h} שע׳`;
    return new Date(t).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const NotificationSection = () => {
    const theme = useTheme();
    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
    const cfg = useAiConfig();
    const { items, unread, open: openItem, markAllRead } = useAiNotifications();
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<any>(null);

    const visible = useMemo(() => items.slice(0, 30), [items]);

    // No assistant configured for this user → no bell.
    if (!cfg) return null;

    const handleToggle = () => setOpen((prev) => !prev);
    const handleClose = (event: MouseEvent | TouchEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) return;
        setOpen(false);
    };

    return (
        <>
            <Box sx={{ ml: 1, mr: 1, display: 'inline-flex', verticalAlign: 'middle' }}>
                <Tooltip title={unread ? `${unread} עדכונים מהעוזר` : 'עדכונים מהעוזר'} placement="bottom">
                    <ButtonBase sx={{ borderRadius: '12px' }} onClick={handleToggle} ref={anchorRef}>
                        <Badge
                            badgeContent={unread}
                            color="error"
                            max={99}
                            invisible={unread === 0}
                            overlap="circular"
                            sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16 } }}
                        >
                            <Avatar
                                variant="rounded"
                                sx={{
                                    ...theme.typography.commonAvatar,
                                    ...theme.typography.mediumAvatar,
                                    height: 34,
                                    width: 34,
                                    color: unread ? '#7c4dff' : '#8762d0',
                                    backgroundColor: open ? '#ede7f6' : 'white',
                                    borderRadius: '12px',
                                    transition: 'all .2s ease-in-out',
                                    '&:hover': { background: '#ede7f6' },
                                }}
                                aria-controls={open ? 'ai-notifications-list' : undefined}
                                aria-haspopup="true"
                            >
                                {unread ? <NotificationsIcon fontSize="small" /> : <NotificationsNoneIcon fontSize="small" />}
                            </Avatar>
                        </Badge>
                    </ButtonBase>
                </Tooltip>
            </Box>
            <Popper
                placement={matchesXs ? 'bottom' : 'bottom-end'}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [matchesXs ? 5 : 0, 14] } }] }}
                style={{ zIndex: theme.zIndex.modal }}
            >
                {({ TransitionProps }) => (
                    <Transitions position={matchesXs ? 'top' : 'top-right'} in={open} {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MainCard
                                    border={false}
                                    elevation={16}
                                    content={false}
                                    boxShadow
                                    shadow={theme.shadows[16]}
                                    sx={{ width: 360, maxWidth: '92vw', direction: 'rtl' }}
                                >
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 1.5, pb: 1 }}>
                                        <Typography variant="subtitle1">עדכונים מהעוזר</Typography>
                                        {unread > 0 && (
                                            <Button size="small" onClick={markAllRead} sx={{ fontSize: '0.75rem' }}>
                                                סמן הכל כנקרא
                                            </Button>
                                        )}
                                    </Stack>
                                    <Divider />
                                    {visible.length === 0 ? (
                                        <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                אין עדכונים חדשים. תוצאות של משימות מתוזמנות ותזכורות יופיעו כאן.
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <List id="ai-notifications-list" dense disablePadding sx={{ maxHeight: 'min(60vh, 480px)', overflowY: 'auto' }}>
                                            {visible.map((n) => (
                                                <ListItemButton
                                                    key={n.id}
                                                    alignItems="flex-start"
                                                    onClick={() => { openItem(n); setOpen(false); }}
                                                    sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 32, mt: 0.25 }}>{kindIcon(n)}</ListItemIcon>
                                                    <ListItemText
                                                        primary={n.title}
                                                        secondary={(
                                                            <>
                                                                {n.text && (
                                                                    <Typography
                                                                        component="span"
                                                                        variant="caption"
                                                                        sx={{ display: 'block', whiteSpace: 'pre-line', color: 'text.secondary' }}
                                                                    >
                                                                        {n.text.length > 180 ? `${n.text.slice(0, 180)}…` : n.text}
                                                                    </Typography>
                                                                )}
                                                                <Typography component="span" variant="caption" sx={{ color: 'text.disabled' }}>
                                                                    {relativeTime(n.created_at)}
                                                                </Typography>
                                                            </>
                                                        )}
                                                        primaryTypographyProps={{ variant: 'subtitle2', sx: { textAlign: 'right' } }}
                                                        secondaryTypographyProps={{ component: 'div', sx: { textAlign: 'right' } }}
                                                    />
                                                </ListItemButton>
                                            ))}
                                        </List>
                                    )}
                                    <Divider />
                                    <Box sx={{ p: 1, textAlign: 'center' }}>
                                        <Button
                                            size="small"
                                            onClick={() => { aiOpenView('scheduler'); setOpen(false); }}
                                            sx={{ fontSize: '0.75rem' }}
                                        >
                                            כל המשימות המתוזמנות
                                        </Button>
                                    </Box>
                                </MainCard>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>
        </>
    );
};

export default NotificationSection;
